import { startTransition, useCallback, useEffect, useState } from "react";
import { type SafeAreaInset, getSafeAreaInset } from "./getSafeAreaInset";

/**
 * React hook to track safe area inset changes
 *
 * Returns the safe area insets that automatically update when the screen
 * orientation changes (e.g., portrait to landscape).
 *
 * Safe area insets account for device-specific UI elements:
 * - **top**: Notch, Dynamic Island, or status bar
 * - **bottom**: Home indicator on Face ID devices
 * - **left/right**: Rounded corners in landscape mode
 *
 * @returns Object containing safe area insets for all four sides
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const safeArea = useSafeAreaInset();
 *
 *   return (
 *     <div style={{
 *       paddingTop: safeArea.top,
 *       paddingBottom: safeArea.bottom,
 *       paddingLeft: safeArea.left,
 *       paddingRight: safeArea.right,
 *     }}>
 *       Content that respects safe areas
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Automatically updates when screen rotates
 * function RotationAwareHeader() {
 *   const { top, left, right } = useSafeAreaInset();
 *
 *   return (
 *     <header style={{
 *       paddingTop: top,
 *       paddingLeft: left,
 *       paddingRight: right,
 *     }}>
 *       Header content
 *     </header>
 *   );
 * }
 * ```
 */
export function useSafeAreaInset(): SafeAreaInset {
  const [inset, setInset] = useState<SafeAreaInset>({ top: 0, bottom: 0, left: 0, right: 0 });

  const updateInset = useCallback(() => {
    startTransition(() => {
      setInset(getSafeAreaInset());
    });
  }, []);

  useEffect(
    function readAfterBrowserSettles() {
      // env(safe-area-inset-*) may return 0 during the initial render because
      // the browser hasn't finished computing viewport layout yet (common in PWA cold launch).
      // requestAnimationFrame defers the read until after the first paint,
      // by which point the browser has resolved the actual safe area values.
      const raf = requestAnimationFrame(updateInset);
      return () => cancelAnimationFrame(raf);
    },
    [updateInset],
  );

  useEffect(
    function handleOrientationChange() {
      window.addEventListener("resize", updateInset);
      window.addEventListener("orientationchange", updateInset);

      return function cleanup() {
        window.removeEventListener("resize", updateInset);
        window.removeEventListener("orientationchange", updateInset);
      };
    },
    [updateInset],
  );

  return inset;
}
